import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  HttpClient,
  HttpClientModule,
  HttpHeaders,
} from '@angular/common/http';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellClassParams,
  GridOptions,
} from 'ag-grid-community';
import { Subscription } from 'rxjs';

type Option = { id: number | string; label: string };

@Component({
  selector: 'app-foreign-order-college',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule, AgGridModule],
  templateUrl: './foreign-order-college.component.html',
  styleUrls: ['./foreign-order-college.component.scss'],
})
export class ForeignOrderCollegeComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('gridWrapper', { static: false })
  gridWrapper!: ElementRef<HTMLDivElement>;

  form: FormGroup;

  countries: Option[] = [];
  courses: Option[] = [];

  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  private gridApi!: GridApi;

  // match rows by id
  gridOptions: GridOptions = {
    getRowId: (params) => params.data?.id,
    singleClickEdit: true,
  };

  loading = false;
  error: string | null = null;
  success: string | null = null;

  private subs: Subscription[] = [];

  // Track edits: key = id, value = new order
  editedMap: Map<number | string, number> = new Map();
  // Track touched rows (clicked) — included in payload even if equal to original
  touchedSet: Set<number | string> = new Set();
  // Keep original values so we can revert on cancel
  originalOrderMap: Map<number | string, number> = new Map();

  saving = false;
  editingMode = false;

  lastSearchParams: { country_id: any; course_id: any } | null = null;

  // ResizeObserver
  private resizeObserver?: ResizeObserver;

  // API endpoints
  private COUNTRY_API = 'https://smscap.in/api/country';
  private FOREIGN_COURSE_API = 'https://smscap.in/api/foreign_course'; // /:countryId
  private FOREIGN_COLLEGE_API = 'https://smscap.in/api/foreign_college'; // /:countryId/:courseId
  private UPDATE_ORDER_API = 'https://smscap.in/api/updateforeignorder';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private cd: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      country_id: [null, Validators.required],
      course_id: [null, Validators.required],
    });

    this.columnDefs = [
      {
        field: 'id',
        headerName: 'ID',
        minWidth: 80,
        sortable: true,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'order',
        headerName: 'Order',
        minWidth: 110,
        filter: 'agNumberColumnFilter',
        valueGetter: (p) => p.data?.order ?? 0,
        cellStyle: { textAlign: 'center' },
        editable: true,
        cellEditor: 'agNumberCellEditor',
        valueSetter: (params) => this.foreignOrderValueSetter(params),
        cellClass: (params: CellClassParams) => {
          const id = params.data?.id;
          return this.editedMap.has(id) ? 'edited-order-cell' : '';
        },
      },
      {
        field: 'college_name',
        headerName: 'College Name',
        minWidth: 260,
        flex: 2,
        sortable: true,
        filter: 'agTextColumnFilter',
      },
      { field: 'address', headerName: 'Address', minWidth: 200, flex: 1 },
      { field: 'hint', headerName: 'Hint', minWidth: 140 },
    ];
  }

  ngOnInit(): void {
    this.loadCountries();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.setGridHeight(), 60);

    try {
      this.resizeObserver = new ResizeObserver(() => {
        this.setGridHeight();
      });
      const header = document.querySelector('.page-header') as HTMLElement;
      const form = document.querySelector('.filter-form') as HTMLElement;
      if (header) this.resizeObserver.observe(header);
      if (form) this.resizeObserver.observe(form);
    } catch (err) {
      console.warn('ResizeObserver not available', err);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.setGridHeight();
  }

  private headers() {
    return new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/json',
    });
  }

  // ----- Loaders -----
  private loadCountries() {
    const s = this.http
      .get<any>(this.COUNTRY_API, { headers: this.headers() })
      .subscribe({
        next: (res) => {
          const arr = this.normalizeArrayResponse(res);
          this.countries = arr.map((o: any, i: number) => ({
            id: o?.id ?? i,
            label: o?.country ?? o?.name ?? `Country ${i}`,
          }));
        },
        error: () => (this.countries = []),
      });
    this.subs.push(s);
  }

  private loadCourses(countryId: number | string) {
    const url = `${this.FOREIGN_COURSE_API}/${countryId}`;
    const s = this.http.get<any>(url, { headers: this.headers() }).subscribe({
      next: (res) => {
        const arr = this.normalizeArrayResponse(res);
        this.courses = arr.map((o: any, i: number) => ({
          id: o?.id ?? i,
          label: o?.course_name ?? o?.name ?? `Course ${i}`,
        }));
      },
      error: () => (this.courses = []),
    });
    this.subs.push(s);
  }

  private normalizeArrayResponse(resp: any): any[] {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (resp.data && Array.isArray(resp.data)) return resp.data;
    const firstArray = Object.values(resp).find((v) => Array.isArray(v)) as
      | any[]
      | undefined;
    return firstArray ?? [];
  }

  // ----- Form submit (search) -----
  onCountryChange(e: any) {
    const country = this.form.value.country_id;
    this.courses = [];
    this.form.patchValue({ course_id: null }, { emitEvent: false });
    if (country) this.loadCourses(country);
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { country_id, course_id } = this.form.value;
    this.lastSearchParams = { country_id, course_id };

    this.loading = true;
    this.error = null;

    const url = `${this.FOREIGN_COLLEGE_API}/${country_id}/${course_id}`;

    const s = this.http.get<any>(url, { headers: this.headers() }).subscribe({
      next: (data) => {
        if (data && Array.isArray(data.data)) this.rowData = data.data;
        else if (Array.isArray(data)) this.rowData = data;
        else this.rowData = this.normalizeArrayResponse(data);

        this.originalOrderMap.clear();
        this.rowData.forEach((r) => {
          this.originalOrderMap.set(r.id, Number(r.order ?? 0));
        });

        this.editedMap.clear();
        this.touchedSet.clear();

        this.cd.detectChanges();
        setTimeout(() => this.sizeColumnsToFit(), 50);
        setTimeout(() => this.setGridHeight(), 80);
      },
      error: () => {
        this.rowData = [];
        this.error = 'Failed to fetch foreign colleges. Please try again.';
      },
      complete: () => (this.loading = false),
    });

    this.subs.push(s);
  }

  // ----- Grid helpers -----
  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    setTimeout(() => this.sizeColumnsToFit(), 100);
    setTimeout(() => this.setGridHeight(), 80);
  }

  sizeColumnsToFit() {
    try {
      if (this.gridApi) this.gridApi.sizeColumnsToFit();
    } catch {}
  }

  onCellClicked(event: any) {
    try {
      if (event?.colDef?.field === 'order') {
        this.editingMode = true;
        const id = event?.data?.id;
        if (id !== undefined && id !== null) {
          this.touchedSet.add(id);
        }
        this.cd.detectChanges();
      }
    } catch {}
  }

  // ----- Value setter for order field -----
  private foreignOrderValueSetter(params: any): boolean {
    const newValueRaw = params.newValue;
    const parsed =
      String(newValueRaw).trim() === ''
        ? 0
        : Number(String(newValueRaw).trim());

    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
      return false;
    }

    const current = Number(params.oldValue ?? params.data.order ?? 0);
    const id = params.data?.id;

    // if matches original then remove from editedMap
    const orig = this.originalOrderMap.get(id) ?? current;
    if (parsed === orig) {
      if (this.editedMap.has(id)) this.editedMap.delete(id);
      // apply value to data for consistency
      params.data.order = parsed;
      // refresh row visual
      try {
        if (this.gridApi)
          this.gridApi.refreshCells({ rowNodes: [params.node], force: true });
      } catch {}
      this.cd.detectChanges();
      return true;
    }

    // set data and track edit
    params.data.order = parsed;
    if (!this.originalOrderMap.has(id)) this.originalOrderMap.set(id, current);
    this.editedMap.set(id, parsed);

    try {
      if (this.gridApi)
        this.gridApi.refreshCells({ rowNodes: [params.node], force: true });
    } catch {}

    this.cd.detectChanges();
    return true;
  }

  // ----- Save / Cancel -----
  saveEdits() {
    try {
      (this.gridApi as any)?.stopEditing?.();
    } catch {}

    setTimeout(() => {
      const idsToSend = new Set<number | string>();
      this.editedMap.forEach((_v, k) => idsToSend.add(k));
      this.touchedSet.forEach((k) => idsToSend.add(k));

      if (idsToSend.size === 0) {
        alert('No changes to save.');
        this.editingMode = false;
        this.cd.detectChanges();
        return;
      }

      const payload: Array<{ id: number; college_order: number }> = [];
      const missing: Array<number | string> = [];

      for (const id of idsToSend) {
        const row = this.rowData.find((r: any) => String(r.id) === String(id));
        if (!row) {
          missing.push(id);
          continue;
        }
        const parsed = String(row.order).trim() === '' ? 0 : Number(row.order);
        if (
          !Number.isFinite(parsed) ||
          !Number.isInteger(parsed) ||
          parsed < 0
        ) {
          this.error = `Invalid order for id ${id}. Please enter a non-negative integer.`;
          this.cd.detectChanges();
          return;
        }
        payload.push({ id: Number(id), college_order: parsed });
      }

      if (missing.length) {
        this.error = `Rows not found for ids: ${missing.join(', ')}`;
        this.cd.detectChanges();
        return;
      }

      this.saving = true;
      this.error = null;
      this.success = null;

      const postSub = this.http
        .post<any>(this.UPDATE_ORDER_API, payload, { headers: this.headers() })
        .subscribe({
          next: () => {
            payload.forEach((p) => {
              this.editedMap.delete(p.id);
              this.touchedSet.delete(p.id);
            });
            this.editingMode = false;
            this.success = 'Updated successfully';
            this.cd.detectChanges();

            // refresh original data from last search (if available)
            if (!this.lastSearchParams) {
              this.saving = false;
              setTimeout(() => {
                this.success = null;
                this.cd.detectChanges();
              }, 2000);
              return;
            }

            const { country_id, course_id } = this.lastSearchParams;
            const url = `${this.FOREIGN_COLLEGE_API}/${country_id}/${course_id}`;

            const getSub = this.http
              .get<any>(url, { headers: this.headers() })
              .subscribe({
                next: (data) => {
                  this.rowData = Array.isArray(data.data)
                    ? data.data
                    : Array.isArray(data)
                    ? data
                    : this.normalizeArrayResponse(data);

                  this.originalOrderMap.clear();
                  this.rowData.forEach((r: any) =>
                    this.originalOrderMap.set(r.id, Number(r.order ?? 0))
                  );

                  try {
                    if (
                      this.gridApi &&
                      typeof (this.gridApi as any).applyTransaction ===
                        'function'
                    ) {
                      this.gridApi.applyTransaction({ update: this.rowData });
                      this.gridApi.refreshCells({ force: true });
                    } else if (this.gridApi) {
                      (this.gridApi as any).setRowData(this.rowData);
                      this.gridApi.refreshCells({ force: true });
                    }
                  } catch (err) {
                    console.warn('Grid refresh fallback triggered', err);
                  }

                  setTimeout(() => {
                    this.success = null;
                    this.cd.detectChanges();
                  }, 2000);
                },
                error: () => {
                  this.error = 'Failed to refresh data after save.';
                  this.cd.detectChanges();
                  setTimeout(() => {
                    this.error = null;
                    this.cd.detectChanges();
                  }, 3000);
                },
                complete: () => {
                  this.saving = false;
                  this.cd.detectChanges();
                },
              });
            this.subs.push(getSub);
          },
          error: (err) => {
            this.error = 'Failed to update order. Please try again.';
            this.saving = false;
            this.cd.detectChanges();
          },
        });

      this.subs.push(postSub);
    }, 50);
  }

  cancelEdits() {
    if (this.editedMap.size === 0 && !this.editingMode) return;

    const changedIds = Array.from(this.editedMap.keys());
    this.rowData.forEach((r) => {
      if (changedIds.includes(r.id)) {
        const orig = this.originalOrderMap.get(r.id);
        if (orig !== undefined) r.order = orig;
      }
    });

    const updatedRows = this.rowData.filter((r) => changedIds.includes(r.id));

    try {
      if (
        this.gridApi &&
        typeof (this.gridApi as any).applyTransaction === 'function'
      ) {
        this.gridApi.applyTransaction({ update: updatedRows });
        this.gridApi.refreshCells({ force: true });
      } else if (this.gridApi) {
        (this.gridApi as any).setRowData(this.rowData);
        this.gridApi.refreshCells({ force: true });
      }
    } catch {}

    this.editedMap.clear();
    this.touchedSet.clear();
    this.editingMode = false;
    this.cd.detectChanges();
  }

  trackById(index: number, item: Option) {
    return item?.id ?? index;
  }

  // Grid height calculation (similar to yours)
  private setGridHeight() {
    try {
      const headerEl = document.querySelector(
        '.page-header'
      ) as HTMLElement | null;
      const formEl = document.querySelector(
        '.filter-form'
      ) as HTMLElement | null;
      const footerEl = document.querySelector(
        '.grid-card .d-flex'
      ) as HTMLElement | null;
      const topPadding = 24;
      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const formH = formEl ? formEl.getBoundingClientRect().height : 0;
      const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
      const other = headerH + formH + footerH + topPadding;
      const viewportH =
        window.innerHeight || document.documentElement.clientHeight;
      const gridH = Math.max(280, Math.floor(viewportH - other));

      const gridEl =
        this.gridWrapper && this.gridWrapper.nativeElement
          ? (this.gridWrapper.nativeElement.querySelector(
              '.order-grid'
            ) as HTMLElement)
          : (document.querySelector('.order-grid') as HTMLElement | null);

      if (gridEl) {
        gridEl.style.height = `${gridH}px`;
        gridEl.style.minHeight = `${gridH}px`;

        if (this.gridApi) {
          try {
            this.gridApi.sizeColumnsToFit();
            this.gridApi.refreshCells({ force: true });
          } catch {}
        }
      }
    } catch (err) {
      console.warn('setGridHeight error', err);
    }
  }
}
